import type { Database } from '@shkola/db-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { must, useDb } from './db';
import type { Audience } from './messages';

// ---------------------------------------------------------------------------------------------
// Menu
// ---------------------------------------------------------------------------------------------
export type Meal = Database['public']['Enums']['meal'];
export const MEALS: ReadonlyArray<Meal> = ['breakfast', 'lunch', 'snack'];

/** The 14 allergens of the law on food information, in the order of its list. */
export const ALLERGENS = [
  'gluten',
  'crustaceans',
  'eggs',
  'fish',
  'peanuts',
  'soy',
  'milk',
  'nuts',
  'celery',
  'mustard',
  'sesame',
  'sulphites',
  'lupin',
  'molluscs',
] as const;
export type Allergen = (typeof ALLERGENS)[number];

export interface MenuItem {
  id: string;
  date: string;
  meal: Meal;
  position: number;
  title: string;
  note: string | null;
  allergens: Allergen[];
}

/** Allergens of a dish the family avoids (chosen on the device, never sent to the server). */
export function avoidedIn(item: Pick<MenuItem, 'allergens'>, avoid: ReadonlyArray<string>) {
  return item.allergens.filter((a) => avoid.includes(a));
}

export function useMenu(schoolId: string | undefined, from: string, to: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['menu', schoolId, from, to],
    enabled: schoolId !== undefined,
    queryFn: async () =>
      must(
        await db
          .from('menu_items')
          .select('id, on_date, meal, position, title, note, allergens')
          .eq('school_id', schoolId as string)
          .gte('on_date', from)
          .lte('on_date', to)
          .order('on_date')
          .order('position'),
      ).map((r): MenuItem => ({
        id: r.id,
        date: r.on_date,
        meal: r.meal,
        position: r.position,
        title: r.title,
        note: r.note,
        allergens: r.allergens as Allergen[],
      })),
  });
}

export interface MenuDraft {
  date: string;
  meal: Meal;
  title: string;
  note: string;
  allergens: ReadonlyArray<Allergen>;
}

export function useMenuActions(schoolId: string) {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => client.invalidateQueries({ queryKey: ['menu', schoolId] });
  return {
    add: useMutation({
      mutationFn: async (d: MenuDraft & { position: number }) =>
        must(
          await db.from('menu_items').insert({
            school_id: schoolId,
            on_date: d.date,
            meal: d.meal,
            position: d.position,
            title: d.title.trim(),
            note: d.note.trim() || null,
            allergens: [...d.allergens],
          }),
        ),
      onSettled: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => must(await db.from('menu_items').delete().eq('id', id)),
      onSettled: invalidate,
    }),
    /** The week starting `from` repeats the week before it (days without dishes only). */
    copyWeek: useMutation({
      mutationFn: async ({ items, shiftDays }: { items: MenuItem[]; shiftDays: number }) => {
        if (items.length === 0) return;
        const shift = (date: string) => {
          const d = new Date(`${date}T12:00:00Z`);
          d.setUTCDate(d.getUTCDate() + shiftDays);
          return d.toISOString().slice(0, 10);
        };
        must(
          await db.from('menu_items').insert(
            items.map((i) => ({
              school_id: schoolId,
              on_date: shift(i.date),
              meal: i.meal,
              position: i.position,
              title: i.title,
              note: i.note,
              allergens: i.allergens,
            })),
          ),
        );
      },
      onSettled: invalidate,
    }),
  };
}

// ---------------------------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------------------------
export type EventKind = Database['public']['Enums']['event_kind'];
export const EVENT_KINDS: ReadonlyArray<EventKind> = [
  'celebration',
  'meeting',
  'trip',
  'exam',
  'holiday',
  'other',
];

export interface SchoolEvent {
  id: string;
  audience: Audience;
  classId: string | null;
  kind: EventKind;
  title: string;
  description: string | null;
  startsOn: string;
  endsOn: string;
  atTime: string | null;
  place: string | null;
}

export function useEvents(schoolId: string | undefined, from: string) {
  const db = useDb();
  return useQuery({
    queryKey: ['events', schoolId, from],
    enabled: schoolId !== undefined,
    queryFn: async () =>
      must(
        await db
          .from('school_events')
          .select(
            'id, audience, class_id, kind, title, description, starts_on, ends_on, at_time, place',
          )
          .eq('school_id', schoolId as string)
          .gte('ends_on', from)
          .order('starts_on')
          .order('at_time', { nullsFirst: true }),
      ).map((r): SchoolEvent => ({
        id: r.id,
        audience: r.audience,
        classId: r.class_id,
        kind: r.kind,
        title: r.title,
        description: r.description,
        startsOn: r.starts_on,
        endsOn: r.ends_on,
        atTime: r.at_time?.slice(0, 5) ?? null,
        place: r.place,
      })),
  });
}

export interface EventDraft {
  audience: Audience;
  classId: string | null;
  kind: EventKind;
  title: string;
  description: string;
  startsOn: string;
  endsOn: string;
  atTime: string;
  place: string;
}

export function useEventActions(schoolId: string) {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => client.invalidateQueries({ queryKey: ['events', schoolId] });
  return {
    create: useMutation({
      mutationFn: async (d: EventDraft) =>
        must(
          await db.from('school_events').insert({
            school_id: schoolId,
            audience: d.audience,
            class_id: d.audience === 'class' || d.audience === 'class_parents' ? d.classId : null,
            kind: d.kind,
            title: d.title.trim(),
            description: d.description.trim() || null,
            starts_on: d.startsOn,
            ends_on: d.endsOn < d.startsOn ? d.startsOn : d.endsOn,
            at_time: d.atTime || null,
            place: d.place.trim() || null,
          }),
        ),
      onSettled: invalidate,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => must(await db.from('school_events').delete().eq('id', id)),
      onSettled: invalidate,
    }),
  };
}

// ---------------------------------------------------------------------------------------------
// Polls
// ---------------------------------------------------------------------------------------------
/** Polls ask adults only: children's polls need separate consents. */
export const POLL_AUDIENCES: ReadonlyArray<Audience> = ['parents', 'class_parents', 'staff'];

export interface Poll {
  id: string;
  audience: Audience;
  classId: string | null;
  question: string;
  multiple: boolean;
  closesAt: string | null;
  createdAt: string;
  authorMemberId: string | null;
  options: { id: string; label: string }[];
  /** my answers (option ids) */
  mine: string[];
}

export function isPollOpen(poll: Pick<Poll, 'closesAt'>, now = Date.now()) {
  return poll.closesAt === null || Date.parse(poll.closesAt) > now;
}

export function usePolls(schoolId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['polls', schoolId],
    enabled: schoolId !== undefined,
    queryFn: async () => {
      const polls = must(
        await db
          .from('polls')
          .select(
            'id, audience, class_id, question, multiple, closes_at, created_at, author_member_id, poll_options (id, label, position)',
          )
          .eq('school_id', schoolId as string)
          .order('created_at', { ascending: false }),
      );
      const votes = must(
        await db
          .from('poll_votes')
          .select('poll_id, option_id')
          .in(
            'poll_id',
            polls.map((p) => p.id),
          ),
      );
      return polls.map((p): Poll => ({
        id: p.id,
        audience: p.audience,
        classId: p.class_id,
        question: p.question,
        multiple: p.multiple,
        closesAt: p.closes_at,
        createdAt: p.created_at,
        authorMemberId: p.author_member_id,
        options: [...p.poll_options]
          .sort((a, b) => a.position - b.position)
          .map((o) => ({ id: o.id, label: o.label })),
        mine: votes.filter((v) => v.poll_id === p.id).map((v) => v.option_id),
      }));
    },
  });
}

export interface PollResults {
  votes: Record<string, number>;
  answered: number;
  audience: number;
}

export function usePollResults(pollId: string, enabled: boolean) {
  const db = useDb();
  return useQuery({
    queryKey: ['poll-results', pollId],
    enabled,
    queryFn: async (): Promise<PollResults> => {
      const rows = must(await db.rpc('poll_results', { p_poll: pollId }));
      return {
        votes: Object.fromEntries(rows.map((r) => [r.option_id, r.votes])),
        answered: rows[0]?.answered ?? 0,
        audience: rows[0]?.audience ?? 0,
      };
    },
  });
}

export interface PollDraft {
  audience: Audience;
  classId: string | null;
  question: string;
  options: string[];
  multiple: boolean;
  closesAt: string | null;
}

export function usePollActions(schoolId: string) {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['polls', schoolId] });
    void client.invalidateQueries({ queryKey: ['poll-results'] });
  };
  return {
    create: useMutation({
      mutationFn: async (d: PollDraft) =>
        must(
          await db.rpc('create_poll', {
            p_school: schoolId,
            p_audience: d.audience,
            // null unless the poll is for the parents of a class
            p_class: d.classId as string,
            p_question: d.question,
            p_options: d.options,
            p_multiple: d.multiple,
            ...(d.closesAt && { p_closes_at: d.closesAt }),
          }),
        ),
      onSettled: invalidate,
    }),
    vote: useMutation({
      mutationFn: async (a: { pollId: string; options: string[] }) =>
        must(await db.rpc('vote_poll', { p_poll: a.pollId, p_options: a.options })),
      onSettled: invalidate,
    }),
    close: useMutation({
      mutationFn: async (a: { pollId: string; remove?: boolean }) =>
        must(await db.rpc('close_poll', { p_poll: a.pollId, p_remove: a.remove ?? false })),
      onSettled: invalidate,
    }),
  };
}
